import type { AppEvent, CompletionLevel, EntryKind, EntryMode, GoalCategory, MeasureShape, Schedule, SessionMode } from './events.js';
import type { StudentPath } from './flavour.js';
import { detectUnlocks } from './reduce.js';
import { rankFor } from './ruleset.js';
import type { Ruleset } from './ruleset.js';
import type { DerivedState } from './state.js';
import { completionAt } from './selectors.js';
import { daysBetween } from './time.js';
import type { StudyDate } from './time.js';

/**
 * COMMANDS — the only place events are created.
 *
 * Every write in the app goes through one of these. Validation, id
 * allocation and event shape live here rather than scattered across UI
 * handlers, which means a rule like the backfill window is enforced once and
 * tested once instead of being reimplemented in every screen that can tick a
 * checkbox.
 *
 * Commands are pure: they take state plus a context and return events. They
 * never persist anything — that's the storage layer's job.
 */

export type CommandContext = {
  now: number;
  tzOffsetMinutes: number;
  /** The caller's current study day. Core has no clock. */
  today: StudyDate;
  newId: () => string;
  /** Stamped onto every event this context produces. See `Base.source`. */
  source?: string;
};

export type CommandError =
  | 'backfill-too-old'
  | 'future-date'
  | 'not-found'
  | 'invalid-input'
  | 'backfill-disabled';

export type CommandResult =
  | { ok: true; events: AppEvent[] }
  | { ok: false; reason: CommandError };

const ok = (...events: AppEvent[]): CommandResult => ({ ok: true, events });
const fail = (reason: CommandError): CommandResult => ({ ok: false, reason });

/** Common event envelope. `date` may differ from today for backfill. */
function envelope(ctx: CommandContext, date: StudyDate = ctx.today) {
  return {
    id: ctx.newId(),
    ts: ctx.now,
    tzOffsetMinutes: ctx.tzOffsetMinutes,
    studyDate: date,
    ...(ctx.source === undefined ? {} : { source: ctx.source }),
  };
}

/**
 * The backfill gate.
 *
 * A week strip invites ticking past cells, which is also the easiest possible
 * streak fraud. One window, checked in one place.
 */
function checkDate(ctx: CommandContext, ruleset: Ruleset, date: StudyDate): CommandError | null {
  const delta = daysBetween(date, ctx.today);
  if (delta < 0) return 'future-date';
  if (delta === 0) return null;
  if (ruleset.backfillWindowDays <= 0) return 'backfill-disabled';
  return delta > ruleset.backfillWindowDays ? 'backfill-too-old' : null;
}

// ── habits ────────────────────────────────────────────────────────────────

export function createHabit(
  ctx: CommandContext,
  input: {
    name: string;
    schedule: Schedule;
    icon?: string;
    floor?: string;
    anchor?: string;
    place?: string;
  },
): CommandResult {
  const name = input.name.trim();
  if (!name) return fail('invalid-input');
  const floor = input.floor?.trim();
  const anchor = input.anchor?.trim();
  const place = input.place?.trim();
  return ok({
    ...envelope(ctx),
    type: 'habit.created',
    habitId: ctx.newId(),
    name,
    schedule: input.schedule,
    ...(input.icon === undefined ? {} : { icon: input.icon }),
    ...(floor ? { floor } : {}),
    ...(anchor ? { anchor } : {}),
    ...(place ? { place } : {}),
  });
}

/** Set or clear the if-then plan. Empty strings clear. */
export function setHabitPlan(
  state: DerivedState,
  ctx: CommandContext,
  habitId: string,
  plan: { anchor?: string; place?: string },
): CommandResult {
  if (!state.habits[habitId]) return fail('not-found');
  return ok({
    ...envelope(ctx),
    type: 'habit.planSet',
    habitId,
    anchor: plan.anchor?.trim() ?? '',
    place: plan.place?.trim() ?? '',
  });
}

/** Set or clear the two-minute version. An empty string clears it. */
export function setHabitFloor(
  state: DerivedState,
  ctx: CommandContext,
  habitId: string,
  floor: string,
): CommandResult {
  if (!state.habits[habitId]) return fail('not-found');
  return ok({ ...envelope(ctx), type: 'habit.floorSet', habitId, floor: floor.trim() });
}

export function renameHabit(
  state: DerivedState,
  ctx: CommandContext,
  habitId: string,
  name: string,
): CommandResult {
  if (!state.habits[habitId]) return fail('not-found');
  if (!name.trim()) return fail('invalid-input');
  return ok({ ...envelope(ctx), type: 'habit.renamed', habitId, name: name.trim() });
}

export function rescheduleHabit(
  state: DerivedState,
  ctx: CommandContext,
  habitId: string,
  schedule: Schedule,
): CommandResult {
  if (!state.habits[habitId]) return fail('not-found');
  return ok({ ...envelope(ctx), type: 'habit.rescheduled', habitId, schedule });
}

export function archiveHabit(
  state: DerivedState,
  ctx: CommandContext,
  habitId: string,
): CommandResult {
  if (!state.habits[habitId]) return fail('not-found');
  return ok({ ...envelope(ctx), type: 'habit.archived', habitId });
}

/**
 * Rearrange the habit list.
 *
 * Takes the full list the student is looking at. Unknown ids are dropped here
 * rather than at fold time so the log never records an order referring to
 * habits that don't exist.
 */
export function reorderHabits(
  state: DerivedState,
  ctx: CommandContext,
  habitIds: readonly string[],
): CommandResult {
  const known = habitIds.filter((id) => state.habits[id] !== undefined);
  if (known.length === 0) return fail('invalid-input');
  return ok({ ...envelope(ctx), type: 'habit.reordered', habitIds: known });
}

/**
 * Tick, untick, or move between levels.
 *
 * Tapping the level that is already recorded undoes it. Tapping the *other*
 * level moves between them — a floor day that turned into a real one is an
 * upgrade, not an undo followed by a redo, and modelling it as one keeps the
 * completion count and the day's activity flag stable through the change.
 *
 * `level` is omitted from the event when it is `full`, so the common case
 * stays byte-identical to what the log held before floors existed.
 */
export function toggleHabit(
  state: DerivedState,
  ctx: CommandContext,
  ruleset: Ruleset,
  habitId: string,
  date: StudyDate = ctx.today,
  level: CompletionLevel = 'full',
): CommandResult {
  if (!state.habits[habitId]) return fail('not-found');
  const problem = checkDate(ctx, ruleset, date);
  if (problem) return fail(problem);

  if (completionAt(state, habitId, date) === level) {
    return ok({ ...envelope(ctx, date), type: 'habit.uncompleted', habitId });
  }

  return ok({
    ...envelope(ctx, date),
    type: 'habit.completed',
    habitId,
    ...(level === 'full' ? {} : { level }),
  });
}

// ── profile ───────────────────────────────────────────────────────────────

/** What the greeting says. Onboarding asks once and it stays skippable —
 *  an empty name means Home greets without one, never with a placeholder. */
export function nameProfile(ctx: CommandContext, name: string): CommandResult {
  const trimmed = name.trim();
  if (!trimmed) return fail('invalid-input');
  return ok({ ...envelope(ctx), type: 'profile.named', name: trimmed });
}

/**
 * The one question onboarding asks about the person rather than the work.
 *
 * No validation beyond the type: every answer is legitimate, including the
 * one that says none of these describes me.
 */
export function setProfilePath(ctx: CommandContext, path: StudentPath): CommandResult {
  return ok({ ...envelope(ctx), type: 'profile.pathSet', path });
}

// ── tasks ─────────────────────────────────────────────────────────────────

export function createTask(
  ctx: CommandContext,
  input: { name: string; track?: string; dueDate?: StudyDate; estimateMinutes?: number },
): CommandResult {
  const name = input.name.trim();
  if (!name) return fail('invalid-input');
  return ok({
    ...envelope(ctx),
    type: 'task.created',
    taskId: ctx.newId(),
    name,
    ...(input.track === undefined ? {} : { track: input.track }),
    ...(input.dueDate === undefined ? {} : { dueDate: input.dueDate }),
    ...(input.estimateMinutes === undefined ? {} : { estimateMinutes: input.estimateMinutes }),
  });
}

export function toggleTask(
  state: DerivedState,
  ctx: CommandContext,
  taskId: string,
): CommandResult {
  const task = state.tasks[taskId];
  if (!task) return fail('not-found');
  return ok({
    ...envelope(ctx),
    type: task.done ? 'task.uncompleted' : 'task.completed',
    taskId,
  });
}

export function archiveTask(
  state: DerivedState,
  ctx: CommandContext,
  taskId: string,
): CommandResult {
  if (!state.tasks[taskId]) return fail('not-found');
  return ok({ ...envelope(ctx), type: 'task.archived', taskId });
}

// ── entries ───────────────────────────────────────────────────────────────

/**
 * Record work done. One command for both capture routes.
 *
 * A timer that has just finished calls this with `mode: 'timed'` (via
 * `completeSession`, which builds the same event); a student typing an hour in
 * afterwards gets `mode: 'manual'`. Everything downstream — the day's minutes,
 * the track totals, corrections, deletion — then behaves identically, which is
 * the entire point of the unification.
 */
export function logEntry(
  ctx: CommandContext,
  ruleset: Ruleset,
  input: {
    track: string;
    minutes: number;
    kind?: EntryKind;
    mode?: EntryMode;
    date?: StudyDate;
    sessionId?: string;
    planMinutes?: number;
    timerMode?: SessionMode;
    intention?: string;
    nextStep?: string;
    recall?: string;
  },
): CommandResult {
  const track = input.track.trim();
  if (!track || !Number.isFinite(input.minutes) || input.minutes <= 0) {
    return fail('invalid-input');
  }
  const date = input.date ?? ctx.today;
  const problem = checkDate(ctx, ruleset, date);
  if (problem) return fail(problem);

  return ok({
    ...envelope(ctx, date),
    type: 'entry.logged',
    entryId: ctx.newId(),
    track,
    minutes: Math.round(input.minutes),
    kind: input.kind ?? 'learn',
    mode: input.mode ?? 'manual',
    ...(input.sessionId === undefined ? {} : { sessionId: input.sessionId }),
    ...(input.planMinutes === undefined ? {} : { planMinutes: input.planMinutes }),
    ...(input.timerMode === undefined ? {} : { timerMode: input.timerMode }),
    ...(input.intention === undefined ? {} : { intention: input.intention }),
    ...(input.nextStep === undefined ? {} : { nextStep: input.nextStep }),
    ...(input.recall === undefined ? {} : { recall: input.recall }),
  });
}

/**
 * Fix an entry.
 *
 * Works on timed entries as well as typed ones — which the old `study.corrected`
 * could not do, because a timer's minutes were never an entry in the first
 * place. A student who left the timer running through dinner can now say so.
 */
export function correctEntry(
  state: DerivedState,
  ctx: CommandContext,
  entryId: string,
  minutes: number,
  patch: { track?: string; kind?: EntryKind } = {},
): CommandResult {
  const entry = state.entries[entryId];
  if (!entry || entry.deleted) return fail('not-found');
  if (!Number.isFinite(minutes) || minutes <= 0) return fail('invalid-input');
  const track = patch.track?.trim();
  if (track !== undefined && !track) return fail('invalid-input');
  return ok({
    ...envelope(ctx, entry.date),
    type: 'entry.corrected',
    entryId,
    minutes: Math.round(minutes),
    ...(track === undefined ? {} : { track }),
    ...(patch.kind === undefined ? {} : { kind: patch.kind }),
  });
}

export function deleteEntry(
  state: DerivedState,
  ctx: CommandContext,
  entryId: string,
): CommandResult {
  const entry = state.entries[entryId];
  if (!entry || entry.deleted) return fail('not-found');
  return ok({ ...envelope(ctx, entry.date), type: 'entry.deleted', entryId });
}

// ── goal & measures ────────────────────────────────────────────────────

/**
 * Start a goal.
 *
 * Only `title`, `category` and `targetDate` are required, because onboarding
 * must not block on thinking. The obstacle especially is worth more later —
 * asked at minute one it returns "laziness"; asked after a missed Wednesday
 * it returns something specific and true. `goal.edited` fills it in then.
 */
export function startGoal(
  ctx: CommandContext,
  input: {
    preset?: string;
    title: string;
    category: GoalCategory;
    targetDate: StudyDate;
    tracks?: string[];
    outcome?: string;
    obstacle?: string;
    doneWhen?: string;
    parts?: string[];
  },
): CommandResult {
  if (!input.title.trim()) return fail('invalid-input');
  if (daysBetween(ctx.today, input.targetDate) <= 0) return fail('invalid-input');
  const parts = input.parts?.map((part) => part.trim()).filter(Boolean);
  return ok({
    ...envelope(ctx),
    type: 'goal.started',
    goalId: ctx.newId(),
    preset: input.preset ?? '',
    title: input.title.trim(),
    category: input.category,
    targetDate: input.targetDate,
    tracks: input.tracks ?? [],
    ...(input.outcome?.trim() ? { outcome: input.outcome.trim() } : {}),
    ...(input.obstacle?.trim() ? { obstacle: input.obstacle.trim() } : {}),
    ...(input.doneWhen?.trim() ? { doneWhen: input.doneWhen.trim() } : {}),
    ...(parts?.length ? { parts } : {}),
  });
}

/** Change the goal's text. Absent fields are unchanged, never cleared. */
export function editGoal(
  ctx: CommandContext,
  state: DerivedState,
  goalId: string,
  patch: {
    title?: string;
    outcome?: string;
    obstacle?: string;
    doneWhen?: string;
    targetDate?: StudyDate;
  },
): CommandResult {
  const goal = state.goals[goalId];
  if (!goal) return fail('not-found');
  if (patch.title !== undefined && !patch.title.trim()) return fail('invalid-input');
  if (patch.targetDate !== undefined && daysBetween(goal.startedOn, patch.targetDate) <= 0) {
    return fail('invalid-input');
  }
  return ok({
    ...envelope(ctx),
    type: 'goal.edited',
    goalId,
    ...(patch.title === undefined ? {} : { title: patch.title.trim() }),
    ...(patch.outcome === undefined ? {} : { outcome: patch.outcome.trim() }),
    ...(patch.obstacle === undefined ? {} : { obstacle: patch.obstacle.trim() }),
    ...(patch.doneWhen === undefined ? {} : { doneWhen: patch.doneWhen.trim() }),
    ...(patch.targetDate === undefined ? {} : { targetDate: patch.targetDate }),
  });
}

/** Replace the ordered work. Re-planning happens as a set, not one at a time. */
export function setGoalParts(
  ctx: CommandContext,
  state: DerivedState,
  goalId: string,
  parts: string[],
): CommandResult {
  if (!state.goals[goalId]) return fail('not-found');
  const cleaned = parts.map((part) => part.trim()).filter(Boolean);
  // Duplicates would make `partsCompleted` ambiguous — a part is identified
  // by its own text, so two identical chapters cannot be told apart.
  if (new Set(cleaned).size !== cleaned.length) return fail('invalid-input');
  return ok({ ...envelope(ctx), type: 'goal.partsSet', goalId, parts: cleaned });
}

export function completeGoalPart(
  ctx: CommandContext,
  state: DerivedState,
  goalId: string,
  part: string,
): CommandResult {
  const goal = state.goals[goalId];
  if (!goal) return fail('not-found');
  if (!goal.parts.includes(part)) return fail('not-found');
  return ok({ ...envelope(ctx), type: 'goal.partCompleted', goalId, part });
}

/** Finish or abandon. Both are honest endings and neither deletes anything. */
export function endGoal(
  ctx: CommandContext,
  state: DerivedState,
  goalId: string,
  status: 'completed' | 'abandoned',
): CommandResult {
  const goal = state.goals[goalId];
  if (!goal) return fail('not-found');
  if (goal.status !== 'active') return fail('invalid-input');
  return ok({ ...envelope(ctx), type: 'goal.ended', goalId, status });
}

/**
 * Record a number, defining the measure if this is the first one.
 *
 * ONE command, because from the student's side there is one act: they type a
 * name and a number. Whether that name is new is the app's problem, not
 * theirs — which is exactly what keeps a "manage your metrics" screen from
 * ever needing to exist (§45).
 *
 * Matching is by name, case-insensitively, because "Physics mock" typed twice
 * is one measure and asking the student to notice that is asking them to do
 * database administration.
 */
export function recordReading(
  state: DerivedState,
  ctx: CommandContext,
  input: {
    measure: string;
    shape: MeasureShape;
    value: number;
    track?: string;
    outOf?: number;
    unit?: string;
    entryId?: string;
  },
): CommandResult {
  const name = input.measure.trim();
  if (!name) return fail('invalid-input');
  if (!Number.isFinite(input.value) || input.value < 0) return fail('invalid-input');

  // A score or a rank without a denominator is meaningless — 62 out of what?
  const needsOutOf = input.shape === 'outOf' || input.shape === 'rank';
  if (needsOutOf && (input.outOf === undefined || input.outOf <= 0)) return fail('invalid-input');
  if (input.shape === 'outOf' && input.outOf !== undefined && input.value > input.outOf) {
    return fail('invalid-input');
  }

  const existing = Object.values(state.measures).find(
    (measure) => measure.name.toLowerCase() === name.toLowerCase(),
  );

  const events: AppEvent[] = [];
  const measureId = existing?.id ?? ctx.newId();

  if (!existing) {
    const track = input.track?.trim();
    const unit = input.unit?.trim();
    events.push({
      ...envelope(ctx),
      type: 'measure.defined',
      measureId,
      name,
      shape: input.shape,
      ...(track ? { track } : {}),
      ...(input.outOf === undefined ? {} : { outOf: input.outOf }),
      ...(unit ? { unit } : {}),
    });
  }

  events.push({
    ...envelope(ctx),
    type: 'reading.recorded',
    readingId: ctx.newId(),
    measureId,
    value: input.value,
    ...(input.entryId === undefined ? {} : { entryId: input.entryId }),
    // Carried per-reading only when it differs from the measure's own, so a
    // series out of 50 with one mock out of 100 stays one series.
    ...(input.outOf !== undefined && input.outOf !== existing?.outOf
      ? { outOf: input.outOf }
      : {}),
  });

  return ok(...events);
}

export function correctReading(
  state: DerivedState,
  ctx: CommandContext,
  readingId: string,
  value: number,
  outOf?: number,
): CommandResult {
  const reading = state.readings[readingId];
  if (!reading || reading.deleted) return fail('not-found');
  if (!Number.isFinite(value) || value < 0) return fail('invalid-input');
  if (outOf !== undefined && outOf <= 0) return fail('invalid-input');
  return ok({
    ...envelope(ctx, reading.date),
    type: 'reading.corrected',
    readingId,
    value,
    ...(outOf === undefined ? {} : { outOf }),
  });
}

export function deleteReading(
  state: DerivedState,
  ctx: CommandContext,
  readingId: string,
): CommandResult {
  const reading = state.readings[readingId];
  if (!reading || reading.deleted) return fail('not-found');
  return ok({ ...envelope(ctx, reading.date), type: 'reading.deleted', readingId });
}

// ── progression bookkeeping ───────────────────────────────────────────────

/**
 * Turn newly-earned progression into permanent facts.
 *
 * Run after every write. Achievements and rank-ups are *detected* by folding
 * but *stored* as events, so a future ruleset can't silently revoke a badge
 * someone already earned (spec §11), and share cards get a dated history.
 */
export function syncProgression(
  state: DerivedState,
  ctx: CommandContext,
  ruleset: Ruleset,
): AppEvent[] {
  const events: AppEvent[] = detectUnlocks(state, ruleset).map((achievementId) => ({
    ...envelope(ctx),
    type: 'achievement.unlocked',
    achievementId,
  }));

  const rank = rankFor(state.power, ruleset);
  if (!state.ranksReached[rank.id] && rank.minPower > 0) {
    events.push({
      ...envelope(ctx),
      type: 'rank.reached',
      rankId: rank.id,
      power: state.power,
    });
  }

  return events;
}
